import { Box, Grid } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import {
    DEFAULT_PLANNING_AGE,
    DEFAULT_RETIREMENT_AGE,
    RETIREMENT_GOAL,
    DEFAULT_CURRENCY_SYMBOL,
    FEATURE_NAMES
} from '../../common/constants';
import { addAgeToDate, getHeaderIconFromType, getYYYYMMDDfromDate } from '../../common/helper';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import {
    CallRunpipePropsType,
    GoalPriorityEnum,
    GoalTitleEnum,
    GoalTypeProps,
    RetirementGoalsFormType
} from '../../common/types/clients-entity';
import { ComboSelect, TextboxInput, Typography } from '../../components/atoms';
import { DrawerComp, FundingSourcesWidget, InfoTooltip, RadioWithOptionalDatePicker } from '../../components/molecules';
import { GoalType } from '../../components/molecules/GoalType/GoalType';
import { GoalPriorityInfoText } from '../../components/molecules/GoalPriorityInfoText/GoalPriorityInfoText';
import { Source } from '../../components/molecules/MonetarySource/MonetarySource';
import { createGoal, saveGoal } from '../../features/client-goals';
import { updateGlobalLoaderState } from '../../features/global/globalSlice';
import { customPalette } from '../../themes/palette';
import {
    deepEqual,
    checkFFEnabled,
    formatCurrency,
    removeNonNumeric,
    priorityOptionsFlow,
    getGoalPriorityEnum,
    getClientJourneyFlowType,
    isMonthYearDatesEnabled
} from '../../utils';
import { setEnableConfirmConsoleNavigation } from '../../features/client';

export interface RetirementGoalProps {
    // eslint-disable-next-line no-unused-vars
    handleClose: (flag: boolean) => void;
    onSubmit?: () => void;
    loading?: boolean;
    heading?: string;
    subHeading?: string;
    selectedGoal?: boolean | GoalTypeProps;
    goalData?: RetirementGoalsFormType;
    yearsInRetirement?: number;
    open: boolean;
    currency?: string;
    callRunpipe?: CallRunpipePropsType;
    goalsWithNoFundingSource?: RetirementGoalsFormType[];
}

export const ItemContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '8px',
    '& a': {
        color: theme.palette.background.paper
    }
}));

export const Footer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    flexDirection: 'column',
    fontSize: '10px',
    height: '100%'
}));

const GoalFormWrapper = styled('div')(() => ({
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    fontSize: '14px'
}));

export const ErrorSpan = styled(Typography)(({ theme }) => ({
    color: theme.palette.berry[400],
    display: 'flex',
    marginTop: 5,
    fontStyle: 'italic'
}));

export const SavedFundsWrapper = styled('div')(() => ({
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column'
}));

export const Wrap = styled('div')(() => ({
    position: 'relative'
}));

interface FrequencyProps {
    selected: boolean;
}

export const Frequency = styled('div')<FrequencyProps>(({ selected }) => ({
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    aspectRatio: '1/1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #E0E0E0',
    cursor: 'pointer',
    backgroundColor: selected ? '#E0E0E0' : 'transparent'
}));

export const Placeholder = styled('div')(() => ({
    height: '21px'
}));

export const StyledInfoText = styled(Typography)(() => ({
    variant: 'body1',
    color: customPalette.universalCard.subTextFontColor,
    fontSize: '13px',
    fontStyle: 'normal',
    lineHeight: '18px',
    fontWeight: 500
}));

export const RetirementGoalsFF = ({
    handleClose,
    selectedGoal,
    open,
    goalData,
    yearsInRetirement,
    callRunpipe,
    goalsWithNoFundingSource
}: RetirementGoalProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const {
        orgInfo,
        goeConfig,
        globalConfig: { currencySymbol }
    } = useAppSelector((state) => state.global);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const isFlow2 = getClientJourneyFlowType(adviceClient?.tenant) === 'flow2';
    const adviceGoals = useAppSelector((state) => state.adviceGoals || []);
    const originalGoalsDataRetirement: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
    const goalPriorityValue = goeConfig?.goalPriorityProbabilities;
    const goalPriorityValueInPercentage =
        goalPriorityValue &&
        Object.fromEntries(
            Object.entries(goalPriorityValue || {}).map(([key, value]) => [key, (value as number) * 100])
        );
    const goalPriorityEnum = getGoalPriorityEnum(goalPriorityValue);
    const priorityOptions = priorityOptionsFlow(t, goalPriorityEnum);

    const investmentSourceData = useMemo(
        () =>
            adviceClient?.accounts.investmentsSources.reduce((acc: Record<string, Source>, curr) => {
                acc[curr.accountId] = {
                    accountNumber: curr.accountId,
                    accountName: curr.accountName,
                    frequency: curr.frequency,
                    isSelected: goalData
                        ? adviceGoals.formFields?.some((goal) =>
                              goal?.data?.sources?.investmentSource?.some(
                                  (inc: any) =>
                                      inc.accountNumber === curr.accountId &&
                                      inc.isSelected &&
                                      goal?.goalId === goalData?.goalId
                              )
                          )
                        : false,
                    recurringContributionsStartDate: getYYYYMMDDfromDate(dayjs(curr.recurringContributionsStartDate)),
                    recurringContributionsEndDate: getYYYYMMDDfromDate(dayjs(curr.recurringContributionsEndDate)),
                    isRCSelected: goalData
                        ? adviceGoals.formFields?.some((goal) =>
                              goal?.data?.sources?.investmentSource?.some(
                                  (inv: { accountNumber: string; isSelected: boolean }) =>
                                      inv.accountNumber === curr.accountId &&
                                      inv.isSelected &&
                                      goal?.goalId === goalData?.goalId
                              )
                          )
                        : false,
                    accountBalance: curr.amount,
                    recurringContributions: curr.recurringContributions || 0,
                    disabled: goalData
                        ? adviceGoals.formFields?.some((goal) =>
                              goal?.data?.sources?.investmentSource?.some(
                                  (inv: { accountNumber: string; isSelected: boolean }) =>
                                      inv.accountNumber === curr.accountId &&
                                      inv.isSelected &&
                                      goal?.goalId !== goalData?.goalId
                              )
                          )
                        : adviceGoals.formFields?.some((goal) =>
                              goal?.data?.sources?.investmentSource?.some(
                                  (inv: { accountNumber: string; isSelected: boolean }) =>
                                      inv.accountNumber === curr.accountId && inv.isSelected
                              )
                          )
                };
                return acc;
            }, {}),
        [adviceGoals.formFields, open]
    );

    const incomeSourceData = useMemo(
        () =>
            adviceClient?.accounts.incomeSources.reduce((acc: Record<string, Source>, curr) => {
                acc[curr.accountId] = {
                    accountNumber: curr.accountId,
                    accountName: curr.sourceName,
                    beginningYear: curr.beginningYear,
                    endYear: curr.endYear,
                    frequency: curr.frequency,
                    isSelected: goalData
                        ? adviceGoals.formFields?.some((goal) =>
                              goal?.data?.sources?.incomeSource?.some(
                                  (inc: any) =>
                                      inc.accountNumber === curr.accountId &&
                                      inc.isSelected &&
                                      goal?.goalId === goalData?.goalId
                              )
                          )
                        : false,
                    accountBalance: curr.frequency === 'Annually' ? curr.amount : 12 * curr.amount,
                    disabled: goalData
                        ? adviceGoals.formFields?.some((goal) =>
                              goal?.data?.sources?.incomeSource?.some(
                                  (inc: any) =>
                                      inc.accountNumber === curr.accountId &&
                                      inc.isSelected &&
                                      goal?.goalId !== goalData?.goalId
                              )
                          )
                        : adviceGoals.formFields?.some((goal) =>
                              goal?.data?.sources?.incomeSource?.some(
                                  (inc: any) => inc.accountNumber === curr.accountId
                              )
                          )
                };
                return acc;
            }, {}),
        [adviceGoals.formFields, open]
    );

    const [incomeSource, setIncomeSource] = useState(incomeSourceData);
    const [investmentSource, setInvestmentSource] = useState(investmentSourceData);
    const [fundingSourceError, setFundingSourceError] = useState<JSX.Element | string | null>(null);
    const [goalAmountTouched, setGoalAmountTouched] = useState(false);
    const [planStartRetirementKey, setPlanStartRetirementKey] = useState<string>('');
    const [targetDateKey, setTargetDateKey] = useState<string>('');
    const incomeSourceSelected = Object.values(incomeSource).filter((source) => source.isSelected);
    const investmentSourceSelected = Object.values(investmentSource).filter((source) => source.isSelected);
    const initialValues = {
        frequency: undefined,
        goalId: '',
        goalName: '',
        goalPriority: undefined,
        goalType: 'Retirement Goal',
        planStartRetirement: null as Dayjs | null,
        planStartRetirementKey: '',
        targetDate: null as Dayjs | null,
        targetDateKey: '',
        targetedRetirementIncome: undefined,
        yearsInRetirement: undefined,
        goalAmount: 0
    };
    const {
        control,
        formState: { errors, isValid },
        trigger,
        reset,
        getValues,
        setValue,
        watch,
        setError,
        clearErrors
    } = useForm<RetirementGoalsFormType>({
        mode: 'all',
        reValidateMode: 'onChange',
        defaultValues: initialValues
    });

    const submitFunds = async () => {
        try {
            const data = getValues();
            await trigger();
            if (
                !goalData &&
                isValid &&
                adviceClient?._id &&
                typeof selectedGoal !== 'boolean' &&
                (incomeSourceSelected.length > 0 || investmentSourceSelected.length > 0)
            ) {
                const isDuplicateName = adviceGoals?.formFields
                    .filter((goal) => (goalData ? goal.goalId !== (goalData as RetirementGoalsFormType).goalId : true))
                    .some((i) => i.data.goalName.toLowerCase() === data.goalName.toLowerCase());
                if (isDuplicateName) {
                    setError('goalName', { message: t('DUPLICATE_NAME') });
                    return;
                } else {
                    clearErrors('goalName');
                }
                dispatch(updateGlobalLoaderState(true));
                const generatedId = uuidv4();
                data.goalType = selectedGoal?.type || RETIREMENT_GOAL;
                data.wealthGoalType = selectedGoal?.title;
                data.sources = {
                    incomeSource: incomeSourceSelected,
                    investmentSource: investmentSourceSelected
                };
                var formatedAmountValTargetIncome = Number(removeNonNumeric(data['targetedRetirementIncome']));
                data['targetedRetirementIncome'] =
                    data['frequency'] === 'Monthly'
                        ? Number(formatedAmountValTargetIncome) * 12
                        : formatedAmountValTargetIncome;
                data['goalAmount'] = data['goalAmount'] ? Number(removeNonNumeric(data['goalAmount'])) : 0;
                data['targetDate'] = getYYYYMMDDfromDate(data['targetDate']);
                data['targetDateKey'] = targetDateKey;
                delete data['yearsInRetirement'];
                delete data['frequency'];
                data['planStartRetirement'] = getYYYYMMDDfromDate(data['planStartRetirement']);
                data['planStartRetirementKey'] = planStartRetirementKey;
                const nextIndex = adviceGoals.formFields.length + 1;
                const newFormField = {
                    goalId: generatedId,
                    data: {
                        ...data,
                        index: nextIndex,
                        ...(checkFFEnabled(FEATURE_NAMES['CONTINUITY_OF_ADVICE_FE_AEX-3603']) &&
                            isFlow2 && {
                                lastRecommendedPortfolioId: null
                            }),
                        // Send goalId on FE for FF-disabled, and for non-flow2 (Advice) when FF is enabled
                        ...((checkFFEnabled(FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']) || !isFlow2) && {
                            goalId: generatedId
                        }),
                        // Mark as newly created on FE so the goal is never tagged with an `update` flag in the payload
                        ...(checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) &&
                            !isFlow2 && {
                                isNew: true
                            }),
                        // createdAt is sent only when FF is disabled
                        ...(!checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) && {
                            createdAt: new Date().toISOString()
                        })
                    }
                };
                let originalGoalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
                const updatedGoalsData = Object.assign({}, originalGoalsData);
                updatedGoalsData.formFields = updatedGoalsData?.formFields
                    ? [...updatedGoalsData?.formFields, newFormField]
                    : [newFormField];
                if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                    updatedGoalsData.formFields.forEach((item) => {
                        // Don't reset an edited goal's `update: true` or tag a new (isNew) goal when adding another goal
                        if (item.goalId !== generatedId && !item.data['isNew'] && item.data['update'] !== true) {
                            item.data['update'] = false;
                        }
                    });
                }
                updatedGoalsData.domainEmail = orgInfo?.orgEmail ?? '';
                updatedGoalsData.goeConfigEmail = orgInfo?.orgEmail ?? '';
                updatedGoalsData.riskProfile = adviceClient.riskProfile;
                updatedGoalsData.assignedClientId = adviceClient._id;
                updatedGoalsData.error = false;
                updatedGoalsData.loading = false;
                if (updatedGoalsData.global?.hasRunPipe) {
                    updatedGoalsData.global = {
                        ...updatedGoalsData.global,
                        status: 'incomplete'
                    };
                }
                if (updatedGoalsData.global?.enablePDFGenerate) {
                    updatedGoalsData.global.enablePDFGenerate = false;
                }
                if (updatedGoalsData.global?.enablePlanDetails) {
                    updatedGoalsData.global.enablePlanDetails = false;
                }
                !updatedGoalsData._id
                    ? await dispatch(createGoal(updatedGoalsData))
                    : await dispatch(saveGoal(updatedGoalsData));
                dispatch(updateGlobalLoaderState(false));
                if (adviceGoals?.global?.isPlanRun) {
                    dispatch(setEnableConfirmConsoleNavigation(true));
                }
                reset();
                setGoalAmountTouched(false);
                setIncomeSource(incomeSourceData);
                setInvestmentSource(investmentSourceData);
                handleClose(false);
            } else {
                handleEditClick();
            }
        } catch (error) {
            console.error('Error in creating goals', error);
        }
    };

    const handleGoalPriorityChange = (selectedValue: GoalPriorityEnum) => {
        setValue('goalPriority', selectedValue, { shouldValidate: true });
    };

    const handleEditClick = async () => {
        const data = getValues();
        const isDuplicateName = adviceGoals?.formFields
            .filter((goal) => (goalData ? goal.goalId !== (goalData as RetirementGoalsFormType).goalId : true))
            .some((i) => i.data.goalName.toLowerCase() === data.goalName.toLowerCase());
        if (isDuplicateName) {
            setError('goalName', { message: t('DUPLICATE_NAME') });
            return;
        } else {
            clearErrors('goalName');
        }
        dispatch(updateGlobalLoaderState(true));
        let originalGoalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
        const updatedGoalsData = Object.assign({}, originalGoalsData);
        updatedGoalsData?.formFields.forEach((item) => {
            const itemData = item['data'];
            if (item.goalId === goalData?.goalId && itemData.goalType === 'retirement') {
                itemData.sources = {
                    incomeSource: incomeSourceSelected,
                    investmentSource: investmentSourceSelected
                };
                if (data['goalAmount']) {
                    let amountVal = data['goalAmount'];
                    var formatedAmountVal = Number(removeNonNumeric(amountVal));
                    itemData['goalAmount'] = formatedAmountVal;
                } else {
                    itemData['goalAmount'] = 0;
                }
                itemData['planStartRetirement'] =
                    typeof data['planStartRetirement'] === 'string'
                        ? data['planStartRetirement']
                        : getYYYYMMDDfromDate(data['planStartRetirement']);
                itemData['planStartRetirementKey'] = planStartRetirementKey;
                itemData['targetDate'] =
                    typeof data['targetDate'] === 'string'
                        ? data['targetDate']
                        : getYYYYMMDDfromDate(data['targetDate']);
                itemData['targetDateKey'] = targetDateKey;
                itemData['goalPriority'] = data['goalPriority'];
                itemData['goalName'] = data['goalName'];
                var formatedAmountValTargetIncome = Number(removeNonNumeric(data['targetedRetirementIncome']));
                itemData['targetedRetirementIncome'] =
                    data['frequency'] === 'Monthly'
                        ? Number(formatedAmountValTargetIncome) * 12
                        : formatedAmountValTargetIncome;
                itemData['frequency'] = data['frequency'];
                if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                    itemData['update'] = true;
                } else {
                    itemData['updatedAt'] = new Date().toISOString();
                }
            } else if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                itemData['update'] = false;
            }
        });
        if (goalData && !deepEqual(originalGoalsDataRetirement.formFields, updatedGoalsData.formFields)) {
            if (updatedGoalsData.global.hasRunPipe) {
                updatedGoalsData.global.status = 'incomplete';
            }
            if (updatedGoalsData.global?.enablePDFGenerate) {
                updatedGoalsData.global.enablePDFGenerate = false;
            }
            if (updatedGoalsData.global?.enablePlanDetails) {
                updatedGoalsData.global.enablePlanDetails = false;
            }
            if (adviceClient.step === 2) {
                updatedGoalsData.response = [];
                await dispatch(saveGoal(updatedGoalsData));
            }
        }
        reset();
        setGoalAmountTouched(false);
        setIncomeSource(incomeSourceData);
        setInvestmentSource(investmentSourceData);
        if (goalsWithNoFundingSource?.length === 0 && adviceClient.step === 3)
            await callRunpipe?.(
                goeConfig?.reallocSchedules ?? [],
                orgInfo?.orgEmail,
                adviceClient,
                adviceGoals,
                dispatch,
                updatedGoalsData
            );
        dispatch(updateGlobalLoaderState(false));
        handleClose(false);
    };

    useEffect(() => {
        if (goalData) {
            // Set touched state if the goal has an amount > 0
            if (goalData?.goalAmount && goalData.goalAmount > 0) {
                setGoalAmountTouched(true);
            } else {
                setGoalAmountTouched(false);
            }

            reset({
                goalAmount: goalData?.goalAmount,
                goalId: goalData?.goalId,
                goalPriority: goalData?.goalPriority,
                targetedRetirementIncome: goalData?.targetedRetirementIncome,
                goalName: goalData?.goalName,
                yearsInRetirement: yearsInRetirement,
                planStartRetirement: dayjs(goalData?.planStartRetirement, 'DD-MM-YYYY').format('DD-MM-YYYY'),
                targetDate: dayjs(goalData?.targetDate, 'DD-MM-YYYY').format('DD-MM-YYYY'),
                frequency: 'Annually'
            });
            // Set the key states from existing goal data
            setPlanStartRetirementKey(goalData?.planStartRetirementKey || '');
            setTargetDateKey(goalData?.targetDateKey || '');
            setIncomeSource(incomeSourceData);
            setInvestmentSource(investmentSourceData);
            checkFundingSourceError(incomeSourceData, 'income');
            checkFundingSourceError(investmentSourceData, 'investment');
        }
    }, []);

    const handleAmountChange = (name: 'targetedRetirementIncome' | 'goalAmount', value: string) => {
        // Set touched state for goalAmount
        if (name === 'goalAmount') {
            setGoalAmountTouched(true);
        }

        const numericValue = removeNonNumeric(value);

        // Handle empty input - allow clearing to blank for goalAmount
        if (name === 'goalAmount' && (numericValue === '' || value.trim() === '')) {
            setValue(name, 0, {
                shouldValidate: true,
                shouldDirty: true
            });
            // Reset touched state when clearing
            setGoalAmountTouched(false);
            return;
        }

        const parsedValue = numericValue === '' ? 0 : Number(numericValue);
        setValue(name, isNaN(parsedValue) ? 0 : parsedValue, {
            shouldValidate: true,
            shouldDirty: true
        });
    };

    const header: any = goalData ? goalData?.title : typeof selectedGoal !== 'boolean' ? selectedGoal?.heading : '';
    const checkIfFormIsEdited = () => {
        let currentGoal = goalData ? JSON.parse(JSON.stringify(goalData)) : JSON.parse(JSON.stringify(initialValues));
        let data = watch();
        // custom hasChanged logic for custom input component FundingSourcesWidget
        const fundingSourceHasChanged =
            !Object.values(investmentSourceData).every(
                (inv) =>
                    (inv.isSelected &&
                        investmentSourceSelected.find(
                            (invSelected) => inv.accountNumber === invSelected.accountNumber
                        )) ||
                    (!inv.isSelected &&
                        !investmentSourceSelected.find(
                            (invSelected) => inv.accountNumber === invSelected.accountNumber
                        ))
            ) ||
            !Object.values(incomeSourceData).every(
                (inv) =>
                    (inv.isSelected &&
                        incomeSourceSelected.find((invSelected) => inv.accountNumber === invSelected.accountNumber)) ||
                    (!inv.isSelected &&
                        !incomeSourceSelected.find((invSelected) => inv.accountNumber === invSelected.accountNumber))
            );
        if (currentGoal && data?.targetedRetirementIncome) {
            var formatedAmountValTargetIncome = Number(removeNonNumeric(data['targetedRetirementIncome']));
            data['targetedRetirementIncome'] =
                data['frequency'] === 'Monthly'
                    ? Number(formatedAmountValTargetIncome) * 12
                    : formatedAmountValTargetIncome;
            data['goalAmount'] = data['goalAmount'];
            if (data['planStartRetirement'])
                data['planStartRetirement'] = getYYYYMMDDfromDate(data['planStartRetirement']);
            data['planStartRetirementKey'] = planStartRetirementKey;
            if (data['targetDate']) data['targetDate'] = getYYYYMMDDfromDate(data['targetDate']);
            data['targetDateKey'] = targetDateKey;
            if (currentGoal?.title) delete currentGoal['title'];
            if (currentGoal?.riskProfile) {
                delete currentGoal?.isIWSApplied;
                delete currentGoal?.accounts;
                delete currentGoal?.riskProfile;
                currentGoal.frequency = 'Annually';
                currentGoal.yearsInRetirement = yearsInRetirement;
                if (currentGoal?.recommendedWealthSplit != undefined) delete currentGoal.recommendedWealthSplit;
                data.goalType = currentGoal.goalType;
            }
        }

        if (currentGoal['yearsInRetirement'])
            currentGoal['yearsInRetirement'] = parseInt(currentGoal['yearsInRetirement']);

        const compareDataWithCurrentGoal = (data: any, currentGoal: any) => {
            if (!data || !currentGoal) return false;
            return Object.keys(data).every((key) => {
                return JSON.stringify(data[key]) === JSON.stringify(currentGoal[key]);
            });
        };

        return !(compareDataWithCurrentGoal(data, currentGoal) && !fundingSourceHasChanged);
    };
    const checkFundingSourceError = (source: Record<string, Source>, type: 'income' | 'investment') => {
        // below constraints are only for Retirement goals, ref: AEX-1811
        if (
            (!Object.values(source).find((sc) => sc.isSelected) &&
                type === 'income' &&
                investmentSourceSelected?.length === 0) ||
            (!Object.values(source).find((sc) => sc.isSelected) &&
                type === 'investment' &&
                incomeSourceSelected?.length === 0)
        )
            setFundingSourceError(
                <Trans
                    i18nKey={`${t('FUNDING_SOURCES_ERROR_MSG')} <br /> ${t('FUNDING_SOURCES_NO_ACC_ERROR_MSG')}`}
                    components={{ br: <br /> }}
                />
            );
        else if (
            (type === 'income' &&
                Object.values(source).find((sc) => sc.isSelected) &&
                investmentSourceSelected?.length === 0) ||
            (type === 'investment' &&
                !Object.values(source).find((sc) => sc.isSelected) &&
                incomeSourceSelected?.length !== 0)
        )
            setFundingSourceError(t('FUNDING_SOURCES_NO_ACC_ERROR_MSG'));
        else setFundingSourceError(null);
    };

    const isRetirementSubmitDisabled = () => {
        const isFundingSourcesEmpty = investmentSourceSelected?.length === 0 && incomeSourceSelected?.length === 0;
        if (!isValid || Object.entries(errors)?.length !== 0 || Boolean(fundingSourceError) || isFundingSourcesEmpty)
            return true;
        return false;
    };

    const handleRetirementDrawerClose = () => {
        reset();
        setGoalAmountTouched(false);
        setIncomeSource(incomeSourceData);
        setInvestmentSource(investmentSourceData);
        setFundingSourceError(null);
        handleClose(false);
    };

    return (
        <DrawerComp
            open={open}
            buttonlabel={t('COMPLETE_TEXT')}
            header={t(header?.toUpperCase() ?? '')}
            drawerIcon={
                <GoalType
                    type={
                        getHeaderIconFromType(
                            goalData
                                ? goalData?.title
                                : typeof selectedGoal !== 'boolean'
                                ? selectedGoal?.title
                                : (t('RETIREMENT') as GoalTitleEnum)
                        )!
                    }
                />
            }
            handleClose={handleRetirementDrawerClose}
            onSubmit={submitFunds}
            disableSubmit={isRetirementSubmitDisabled()}
            hasChange={checkIfFormIsEdited()}
            heapId="complete-retirement-goals-drawer"
        >
            <GoalFormWrapper>
                <StyledInfoText label={t('RETIREMENT_DRAWER_LABEL_TEXT')}></StyledInfoText>
                <ItemContainer sx={{ alignItems: 'flex-start', width: '100%' }}>
                    <Typography
                        variant="labels.large"
                        sx={{ color: theme.palette.neutral[300] }}
                        label={`${t('TEXT_GOAL_NAME')} *`}
                    />
                    <Controller
                        name="goalName"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                            },
                            pattern: {
                                value: /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i,
                                message: t('TEXT_FULLNAME_VALIDATION')
                            }
                        }}
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                            <TextboxInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                inputRef={ref}
                                size="small"
                                error={!!errors.goalName}
                                type="text"
                                data-testid="retirement-goal-name"
                                placeholder={t('TEXT_GOAL_NAME')}
                                fullWidth
                            />
                        )}
                    />
                    {errors.goalName ? (
                        <ErrorSpan variant="subtitle2" role="alert" label={errors.goalName.message} />
                    ) : null}
                </ItemContainer>
                <ItemContainer sx={{ alignItems: 'flex-start', width: '100%' }}>
                    <Grid container alignItems="center">
                        <Typography
                            variant="labels.large"
                            sx={{ color: theme.palette.neutral[300] }}
                            label={`${t('TEXT_GOAL_PRIORITY')} *`}
                        />
                        <InfoTooltip
                            message={<GoalPriorityInfoText values={goalPriorityValueInPercentage} />}
                            placement="bottom"
                        />
                    </Grid>
                    <Controller
                        name="goalPriority"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                            }
                        }}
                        render={({ field }) => (
                            <ComboSelect
                                {...field}
                                onSelectChange={handleGoalPriorityChange}
                                size="small"
                                selectedValue={field.value ?? ''}
                                options={priorityOptions}
                                data-testid="goal-priority"
                            />
                        )}
                    />
                    {errors.goalPriority && (
                        <ErrorSpan variant="subtitle2" role="alert" label={errors.goalPriority.message} />
                    )}
                </ItemContainer>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                    <ItemContainer
                        sx={{
                            alignItems: 'flex-start',
                            width: '100%',
                            justifyContent: 'flex-start'
                        }}
                    >
                        <Grid container alignItems="center" style={{ flexWrap: 'nowrap' }}>
                            <Typography
                                variant="labels.large"
                                sx={{ color: theme.palette.neutral[300], whiteSpace: 'nowrap' }}
                                label={`${t('INCOME_GOAL_AMOUNT')}*`}
                            />
                            <InfoTooltip
                                message={
                                    <Trans
                                        i18nKey="INCOME_GOAL_AMOUNT_INFO_TEXT"
                                        components={{ strong: <strong />, br: <br /> }}
                                    />
                                }
                            />
                        </Grid>
                        <Controller
                            name="targetedRetirementIncome"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                                },
                                validate: {
                                    weight: (value) =>
                                        value !== undefined && value < 0 ? t('TEXT_AMOUNT_VALIDATION') : undefined
                                }
                            }}
                            render={({ field: { onBlur, value, ref } }) => (
                                <TextboxInput
                                    onBlur={() => {
                                        onBlur();
                                    }}
                                    onChange={(e) => {
                                        handleAmountChange('targetedRetirementIncome', e.target.value);
                                    }}
                                    value={value ? formatCurrency(value, true) : ''}
                                    inputRef={ref}
                                    size="small"
                                    error={!!errors.targetedRetirementIncome}
                                    type="text"
                                    data-testid="retirement-goal-amount"
                                    placeholder={currencySymbol || DEFAULT_CURRENCY_SYMBOL}
                                    iconPlacement="start"
                                    fullWidth
                                />
                            )}
                        />
                        {errors.targetedRetirementIncome ? (
                            <ErrorSpan
                                variant="subtitle2"
                                role="alert"
                                label={errors.targetedRetirementIncome.message}
                            />
                        ) : null}
                    </ItemContainer>
                    <ItemContainer
                        sx={{
                            alignItems: 'flex-start',
                            width: '100%',
                            justifyContent: 'flex-start'
                        }}
                    >
                        <Typography
                            variant="labels.large"
                            sx={{ color: theme.palette.neutral[300] }}
                            label={`${t('FREQUENCY')} *`}
                        />
                        <Controller
                            name="frequency"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                                }
                            }}
                            render={({ field: { onChange, onBlur, value, name } }) => (
                                <ComboSelect
                                    onSelectChange={onChange}
                                    onBlur={onBlur}
                                    selectedValue={value || ''}
                                    name={`retirement-${name}`}
                                    placeholder={t('TEXT_SELECT')}
                                    options={[
                                        { label: t('ANNUALLY'), value: 'Annually' },
                                        { label: t('MONTHLY'), value: 'Monthly' }
                                    ]}
                                />
                            )}
                        />
                        {errors.frequency && (
                            <ErrorSpan variant="subtitle2" role="alert" label={errors.frequency.message} />
                        )}
                    </ItemContainer>
                </Box>
                <Typography variant="inherit" label={`${t('RETIREMENT_GOALS_TEXT')} *`} />
                <div className="go-horizontal-align-drawer">
                    <ItemContainer sx={{ alignItems: 'flex-start', width: '100%' }}>
                        <Grid container alignItems="center">
                            <Typography
                                variant="labels.large"
                                sx={{ color: theme.palette.neutral[300] }}
                                label={`${t('STARTING_AT_LABEL')}*`}
                            />
                        </Grid>
                        <Controller
                            name="planStartRetirement"
                            control={control}
                            rules={{
                                required: true,
                                validate: {
                                    minDate: (value) => {
                                        if (
                                            dayjs(value, 'DD-MM-YYYY').isBefore(
                                                dayjs(),
                                                isMonthYearDatesEnabled() ? 'month' : undefined
                                            )
                                        )
                                            return t('TEXT_VALID_DATE_ERROR');
                                    }
                                }
                            }}
                            render={({ field }) => (
                                <RadioWithOptionalDatePicker
                                    showCustomDateField={false}
                                    hasError={
                                        !!errors.planStartRetirement && errors.planStartRetirement.type !== 'required'
                                    }
                                    field={{
                                        ...field,
                                        onChange: (arg: string) => {
                                            field.onChange(arg);
                                            trigger('targetDate');
                                        }
                                    }}
                                    selectedKey={planStartRetirementKey}
                                    onSelectedKeyChange={setPlanStartRetirementKey}
                                    enabled={!!watch().frequency}
                                    isMMYYYY={isMonthYearDatesEnabled()}
                                    isYYYY={!isMonthYearDatesEnabled() && watch().frequency === 'Annually'}
                                    options={[
                                        {
                                            label: (
                                                <>
                                                    <Typography
                                                        variant="labels.largeDemibold"
                                                        label={t('CLIENT_RET_DATE_LABEL')}
                                                    />
                                                    <Typography
                                                        variant="labels.large"
                                                        label={`${addAgeToDate(
                                                            adviceClient?.clientDob,
                                                            adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE,
                                                            'MMMM YYYY'
                                                        )}, ${t('AGE_LABEL')} ${
                                                            adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE
                                                        }`}
                                                    />
                                                </>
                                            ),
                                            value: addAgeToDate(
                                                adviceClient?.clientDob,
                                                adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE
                                            ),
                                            disabled: !watch().frequency
                                        },
                                        adviceClient?.relationship?.[0]?.age && {
                                            label: (
                                                <>
                                                    <Typography
                                                        variant="labels.largeDemibold"
                                                        label={t('COCLIENT_RET_DATE_LABEL')}
                                                    />
                                                    <Typography
                                                        variant="labels.large"
                                                        label={`${addAgeToDate(
                                                            adviceClient?.relationship?.[0]?.age,
                                                            adviceClient?.relationship?.[0]?.retirementAge ||
                                                                DEFAULT_RETIREMENT_AGE,
                                                            'MMMM YYYY'
                                                        )}, ${t('AGE_LABEL')} ${
                                                            adviceClient?.relationship?.[0]?.retirementAge ||
                                                            DEFAULT_RETIREMENT_AGE
                                                        }`}
                                                    />
                                                </>
                                            ),
                                            value: addAgeToDate(
                                                adviceClient?.relationship?.[0]?.age,
                                                adviceClient?.relationship?.[0]?.retirementAge || DEFAULT_RETIREMENT_AGE
                                            ),
                                            disabled: !watch().frequency
                                        }
                                    ]}
                                />
                            )}
                        />
                        {errors.planStartRetirement && (
                            <ErrorSpan variant="subtitle2" role="alert" label={errors.planStartRetirement.message} />
                        )}
                    </ItemContainer>
                    <ItemContainer sx={{ alignItems: 'flex-start', width: '100%' }}>
                        <Grid container alignItems="center">
                            <Typography
                                variant="labels.large"
                                sx={{ color: theme.palette.neutral[300] }}
                                label={`${t('ENDING_AT_LABEL')}*`}
                            />
                        </Grid>
                        <Controller
                            name={'targetDate'}
                            control={control}
                            rules={{
                                required: true,
                                validate: {
                                    validate: (value) => {
                                        if (
                                            dayjs(value, 'DD-MM-YYYY').isBefore(
                                                dayjs(),
                                                isMonthYearDatesEnabled() ? 'month' : undefined
                                            )
                                        )
                                            return t('TEXT_VALID_DATE_ERROR');
                                        if (!watch().planStartRetirement) return;
                                        if (
                                            dayjs(value, 'DD-MM-YYYY').isBefore(
                                                dayjs(watch().planStartRetirement, 'DD-MM-YYYY').add(2, 'years')
                                            )
                                        )
                                            return t('MIN_TWO_YEARS_ERROR_MSG');
                                    }
                                }
                            }}
                            render={({ field }) => (
                                <RadioWithOptionalDatePicker
                                    showCustomDateField={false}
                                    field={{
                                        ...field,
                                        onChange: (arg: string) => {
                                            field.onChange(arg);
                                            trigger('planStartRetirement');
                                        }
                                    }}
                                    selectedKey={targetDateKey}
                                    onSelectedKeyChange={setTargetDateKey}
                                    hasError={!!errors.targetDate && errors.targetDate.type !== 'required'}
                                    options={[
                                        {
                                            label: (
                                                <>
                                                    <Typography
                                                        variant="labels.largeDemibold"
                                                        label={t('CLIENT_PLAN_DATE_LABEL')}
                                                    />
                                                    <Typography
                                                        variant="labels.large"
                                                        label={`${addAgeToDate(
                                                            adviceClient?.clientDob,
                                                            adviceClient?.planningAge || DEFAULT_PLANNING_AGE,
                                                            'MMMM YYYY'
                                                        )}, ${t('AGE_LABEL')} ${
                                                            adviceClient?.planningAge || DEFAULT_PLANNING_AGE
                                                        }`}
                                                    />
                                                </>
                                            ),
                                            value: addAgeToDate(
                                                adviceClient?.clientDob,
                                                adviceClient?.planningAge || DEFAULT_PLANNING_AGE
                                            ),
                                            disabled: !watch().frequency
                                        },
                                        adviceClient?.relationship?.[0]?.age && {
                                            label: (
                                                <>
                                                    <Typography
                                                        variant="labels.largeDemibold"
                                                        label={t('COCLIENT_PLAN_DATE_LABEL')}
                                                    />
                                                    <Typography
                                                        variant="labels.large"
                                                        label={`${addAgeToDate(
                                                            adviceClient?.relationship?.[0]?.age,
                                                            adviceClient?.relationship?.[0]?.planningAge ||
                                                                DEFAULT_PLANNING_AGE,
                                                            'MMMM YYYY'
                                                        )}, ${t('AGE_LABEL')} ${
                                                            adviceClient?.relationship?.[0]?.planningAge ||
                                                            DEFAULT_PLANNING_AGE
                                                        }`}
                                                    />
                                                </>
                                            ),
                                            value: addAgeToDate(
                                                adviceClient?.relationship?.[0]?.age,
                                                adviceClient?.relationship?.[0]?.planningAge || DEFAULT_PLANNING_AGE
                                            ),
                                            disabled: !watch().frequency
                                        }
                                    ]}
                                    isMMYYYY={isMonthYearDatesEnabled()}
                                    isYYYY={!isMonthYearDatesEnabled() && watch().frequency === 'Annually'}
                                    enabled={!!watch().frequency}
                                />
                            )}
                        />
                        {errors.targetDate ? (
                            <ErrorSpan variant="subtitle2" role="alert" label={errors.targetDate.message} />
                        ) : null}
                    </ItemContainer>
                </div>
                <ItemContainer sx={{ alignItems: 'flex-start', width: '100%' }}>
                    <Grid container alignItems="center">
                        <Typography variant="labels.large" label={`${t('GOAL_SUMMARY_BEQUEST_AMOUNT_TEXT')}`} />
                        <InfoTooltip
                            message={
                                <Trans
                                    i18nKey="GOAL_SUMMARY_BEQUEST_AMOUNT_TEXT_INFO_TEXT"
                                    components={{ strong: <strong />, br: <br /> }}
                                />
                            }
                        />
                    </Grid>
                    <Controller
                        name="goalAmount"
                        control={control}
                        rules={{
                            validate: {
                                weight: (value) => (value && value < 0 ? t('TEXT_AMOUNT_VALIDATION') : undefined)
                            }
                        }}
                        render={({ field: { onBlur, value, ref } }) => (
                            <TextboxInput
                                onBlur={() => {
                                    onBlur();
                                }}
                                onChange={(e) => {
                                    handleAmountChange('goalAmount', e.target.value);
                                }}
                                value={
                                    // Show blank if not touched and value is 0
                                    value === 0 && !goalAmountTouched
                                        ? ''
                                        : goalAmountTouched && value !== null && value !== undefined && value >= 0
                                        ? formatCurrency(value, true) // noDecimal: true
                                        : ''
                                }
                                inputRef={ref}
                                size="small"
                                error={!!errors.goalAmount}
                                type="text"
                                placeholder={currencySymbol || DEFAULT_CURRENCY_SYMBOL}
                                data-testid="goal-amount"
                                fullWidth
                                iconPlacement="start"
                            />
                        )}
                    />
                    {errors.goalAmount ? (
                        <ErrorSpan variant="subtitle2" role="alert" label={errors.goalAmount.message} />
                    ) : null}
                </ItemContainer>
                <FundingSourcesWidget
                    incomeSource={incomeSource}
                    onIncomeSourceChange={(source) => {
                        setIncomeSource(source);
                        checkFundingSourceError(source, 'income');
                    }}
                    investmentSource={investmentSource}
                    onInvestmentSourceChange={(source) => {
                        setInvestmentSource(source);
                        checkFundingSourceError(source, 'investment');
                    }}
                    errorMsg={fundingSourceError}
                />
            </GoalFormWrapper>
        </DrawerComp>
    );
};
